/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  Paper,
  Typography,
  Breadcrumbs,
  Link,
  Box,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { formatDate } from '@ink-ai/portal/common/utils';
import { RootState } from '@ink-ai/portal/reducers';
import { CreateReferenceStoreResponseDtoStatusEnum } from '@ink-ai/insight-service-sdk';
import { getReferenceStoreById } from '@ink-ai/portal/reducers/reference-store';
import { PaperCard } from '../../components/Common';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import { makeRequest } from '@ink-ai/portal/common/requestHelper';

export const ReferenceStoreDetail: React.FC = () => {
  const { t } = useTranslation();
  const formatter = new Intl.NumberFormat('en-US');
  const { id: referenceStoreUuid } = useParams<{ id: string }>();
  const dispatch = useDispatch<any>();
  const referenceStore = useSelector(
    (state: RootState) => state.referenceStore,
  );
  const navigate = useNavigate();
  const location = useLocation();
  const isReferenceManagementDetail = location.pathname.startsWith('/reference-management/');
  const backTo = (location.state as any)?.from ?? (isReferenceManagementDetail ? '/reference-management' : '/reference-store');

  // 统一将 updatedAt 转换为毫秒数的 number；若无法转换则返回 undefined
  const safeUpdatedAt = React.useMemo(() => {
    const v: any = referenceStore.selectedReferenceStore?.updatedAt;
    if (v == null) return undefined;
    if (typeof v === 'number') return v;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  }, [referenceStore.selectedReferenceStore?.updatedAt]);

  useEffect(() => {
    if (referenceStoreUuid) {
      dispatch(getReferenceStoreById(referenceStoreUuid));
    }
  }, [dispatch, referenceStoreUuid]);

  return (
    <Container maxWidth="lg" sx={{ mt: 1, mb: 1 }}>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ m: 0 }}>
          <Link
            color="inherit"
            onClick={() => navigate('/')}
            sx={{ textDecoration: 'none', cursor: 'pointer' }}
          >
            {t('referenceStore.navigation.home')}
          </Link>
          <Link
            color="inherit"
            onClick={() => navigate(backTo)}
            sx={{ textDecoration: 'none', cursor: 'pointer' }}
          >
            {isReferenceManagementDetail
              ? t('referenceStore.navigation.referenceManagement')
              : t('referenceStore.navigation.referenceStore')}
          </Link>
          <Typography color="textPrimary">
            {referenceStore.selectedReferenceStore?.name ||
              t('referenceStore.navigation.loading')}
          </Typography>
        </Breadcrumbs>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<ArrowBackOutlinedIcon />}
          onClick={() => navigate(backTo)}
        >
          {t('referenceStore.actions.back')}
        </Button>
      </Box>

      <PaperCard>
        <Typography variant="h5" sx={{ textAlign: 'left' }}>
          {referenceStore.selectedReferenceStore?.name ||
            t('referenceStore.navigation.loading')}
        </Typography>
        <Typography variant="subtitle1" sx={{ textAlign: 'left', mb: 2 }}>
          {t('referenceStore.detail.settings')}
        </Typography>

        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', textAlign: 'left' }}
            >
              {t('referenceStore.detail.fields.terms')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'left', color: 'gray' }}
            >
              {referenceStore.selectedReferenceStore?.status ===
              CreateReferenceStoreResponseDtoStatusEnum.Ready
                ? formatter.format(
                    referenceStore.selectedReferenceStore?.itemCount,
                  )
                : t('referenceStore.detail.values.na')}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', textAlign: 'left' }}
            >
              {t('referenceStore.detail.fields.languages')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'left', color: 'gray' }}
            >
              {referenceStore.selectedReferenceStore?.status ===
              CreateReferenceStoreResponseDtoStatusEnum.Ready
                ? (Array.isArray(referenceStore.selectedReferenceStore?.languages) &&
                   referenceStore.selectedReferenceStore?.languages.length > 0
                    ? referenceStore.selectedReferenceStore?.languages.join(', ')
                    : t('referenceStore.detail.values.na'))
                : t('referenceStore.detail.values.na')}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', textAlign: 'left' }}
            >
              {t('referenceStore.detail.fields.status')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'left', color: 'gray' }}
            >
              {referenceStore.selectedReferenceStore?.status
                ?.charAt(0)
                .toUpperCase() +
                referenceStore.selectedReferenceStore?.status
                  ?.slice(1)
                  .toLowerCase()}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', textAlign: 'left' }}
            >
              {t('referenceStore.detail.fields.storage')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'left', color: 'gray' }}
            >
              {t('referenceStore.detail.values.openSearch')}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', textAlign: 'left' }}
            >
              {t('referenceStore.detail.fields.indexName')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'left', color: 'gray' }}
            >
              {referenceStore.selectedReferenceStore?.indexName ||
                t('referenceStore.detail.values.na')}
            </Typography>
          </Grid>

          <Grid item xs={4}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 'bold', textAlign: 'left' }}
            >
              {t('referenceStore.detail.fields.updatedAt')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ textAlign: 'left', color: 'gray' }}
            >
              {formatDate(safeUpdatedAt)}
            </Typography>
          </Grid>
          <Grid item xs={4}></Grid>
        </Grid>
      </PaperCard>

      {referenceStore.selectedReferenceStore?.status === CreateReferenceStoreResponseDtoStatusEnum.Ready && (
        <ReferenceItemsList referenceStoreId={referenceStoreUuid!} />
      )}
    </Container>
  );
};

const ReferenceItemsList: React.FC<{ referenceStoreId: string }> = ({ referenceStoreId }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');

  const fetchItems = async () => {
    try {
      const data = await makeRequest({
        method: 'GET',
        url: `/api/v1/referenceStore/${referenceStoreId}/items`,
        params: { page: page + 1, pageSize: rowsPerPage, search: search || undefined },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Failed to load reference items', e);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [referenceStoreId, page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    fetchItems();
  };

  return (
    <PaperCard sx={{ mt: 2 }}>
      <Box sx={{ mb: 2 }}>
        <TextField
          label={t('common.search') || 'Search'}
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {items.length > 0 && Object.keys(items[0].languages || {}).map((lang) => (
                <TableCell key={lang} sx={{ fontWeight: 'bold' }}>{lang.toUpperCase()}</TableCell>
              ))}
              {items.length === 0 && (
                <>
                  <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Target</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                {Object.values(item.languages || {}).map((val, idx) => (
                  <TableCell key={idx} sx={{ maxWidth: 500, wordBreak: 'break-word' }}>
                    {String(val)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No items found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
      />
    </PaperCard>
  );
};
